import { observer } from 'mobx-react-lite'
import type { ReactNode } from 'react'
import { MdOutlineCalendarMonth, MdOutlineInbox, MdOutlineLockOpen, MdRefresh } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'
import { pwaManager } from '@/Ship/Pwa/PwaManager'

import { driverDashboardManager } from '../../Stores/DriverDashboardManager'
import { myShiftStore } from '../../Stores/MyShiftStore'

import styles from './EmptyTasksState.module.css'

interface StateProps {
    icon: ReactNode
    title: string
    text: string
    children?: ReactNode
}

const State = ({ icon, title, text, children }: StateProps) => (
    <div className={cx(styles, 'wrap')} data-testid="empty-tasks">
        <div className={cx(styles, 'icon')}>{icon}</div>
        <div className={cx(styles, 'title')}>{title}</div>
        <div className={cx(styles, 'text')}>{text}</div>
        {children}
    </div>
)

/**
 * Порожній список завдань водія: пояснює причину й наступний крок — відкрити зміну, створити
 * її (відкрити нічого), звернутися до диспетчера (немає змін, а створити не можна — напр.
 * офлайн) або чекати на призначення в межах активної зміни.
 */
export const EmptyTasksState = observer(() => {
    const offline = pwaManager.isOffline

    if (myShiftStore.waitingForShift) {
        if (myShiftStore.canCreate) {
            return (
                <State
                    icon={<MdOutlineCalendarMonth />}
                    title="Створіть зміну"
                    text="На сьогодні змін немає. Створіть зміну кнопкою «Створити зміну» в блоці вище — завдання з'являться після її початку."
                />
            )
        }
        return myShiftStore.pending.length > 0 ? (
            <State
                icon={<MdOutlineLockOpen />}
                title="Відкрийте зміну"
                text="Завдання з'являться тут одразу після відкриття зміни — кнопка «Відкрити зміну» в блоці вище."
            />
        ) : (
            <State
                icon={<MdOutlineCalendarMonth />}
                title="Немає запланованих змін"
                text="Зверніться до диспетчера, щоб він запланував вам зміну."
            />
        )
    }

    const active = myShiftStore.active
    const scope = [active?.vehicle?.title, active?.cluster?.title].filter(Boolean) as string[]

    return (
        <State
            icon={<MdOutlineInbox />}
            title="Нових завдань немає"
            text="Щойно диспетчер призначить завдання, ви отримаєте сповіщення, і воно з'явиться в цьому списку."
        >
            {scope.length > 0 && (
                <div className={cx(styles, 'chips')}>
                    {scope.map((item) => (
                        <span key={item} className={cx(styles, 'chip')}>
                            {item}
                        </span>
                    ))}
                </div>
            )}
            {offline ? (
                <div className={cx(styles, 'note')}>Немає з'єднання — список оновиться, коли з'явиться мережа</div>
            ) : (
                <button
                    type="button"
                    className="pwa-btn pwa-btn--ghost pwa-btn--compact"
                    onClick={() => driverDashboardManager.fetchAssignments()}
                >
                    <MdRefresh /> Оновити список
                </button>
            )}
        </State>
    )
})
